<template>
	<span class="inline-block" ref="referenceElement">
		<!-- Bubble focus events so slotted links keep their native tab order. -->
		<span
			class="inline-flex items-center tooltip-trigger"
			:tabindex="disabled ? -1 : triggerTabIndex"
			:aria-describedby="isOpen ? contentId : null"
			@mouseenter="onEnter"
			@mouseleave="onLeave"
			@focusin="onEnter"
			@focusout="onLeave"
			@keydown.esc="hideNow"
		>
			<slot />
		</span>

		<!-- Content -->
		<portal to="tooltip-root" v-if="usePortal">
			<div
				v-show="isOpen"
				:class="[bodyClasses, textAlignmentClass]"
				ref="floatingElement"
				:id="contentId"
				role="tooltip"
				:style="floatingStyle"
				@mouseenter="onTooltipEnter"
				@mouseleave="onTooltipLeave"
			>
				<div>
					<slot name="content">
						{{ content }}
					</slot>
				</div>
				<div
					v-if="arrow"
					ref="arrowElement"
					class="absolute w-2 h-2 transform rotate-45"
					:class="arrowClasses"
					:style="arrowStyle"
				/>
			</div>
		</portal>

		<!-- Non-portal fallback -->
		<div
			v-else
			v-show="isOpen"
			:class="[bodyClasses, textAlignmentClass]"
			ref="floatingElement"
			:id="contentId"
			role="tooltip"
			:style="floatingStyle"
			@mouseenter="onTooltipEnter"
			@mouseleave="onTooltipLeave"
		>
			<div>
				<slot name="content">
					{{ content }}
				</slot>
			</div>

			<div
				v-if="arrow"
				ref="arrowElement"
				class="absolute w-2 h-2 transform rotate-45"
				:class="arrowClasses"
				:style="arrowStyle"
			/>
		</div>
	</span>
</template>

<script>
import {
	computePosition,
	autoUpdate,
	offset,
	flip,
	shift,
	arrow as arrowMw
} from '@floating-ui/dom'

let uid = 0

export default {
	name: 'Tooltip',
	props: {
		tone: { type: String, default: 'dark' },
		textAlign: {
			type: String,
			default: 'center',
			validator: value => ['left', 'center', 'right'].includes(value)
		},
		reference: {
			type: [Object],
			default: null
		},
		content: {
			type: String,
			default: ''
		},
		placement: {
			type: String,
			default: 'top'
		},
		offset: {
			type: Number,
			default: 8
		},
		open: {
			type: Boolean,
			default: null
		},
		openDelay: {
			type: Number,
			default: 50
		},
		closeDelay: {
			type: Number,
			default: 50
		},
		interactive: {
			type: Boolean,
			default: false
		},
		usePortal: {
			type: Boolean,
			default: true
		},
		disabled: {
			type: Boolean,
			default: false
		},
		maxWidth: {
			type: [Number, String],
			default: 260
		},
		arrow: {
			type: Boolean,
			default: true
		},
		triggerTabIndex: {
			type: [Number, String],
			default: null
		},
		zIndex: {
			type: Number,
			default: 9999
		},
		flip: {
			type: Boolean,
			default: true
		}
	},

	data() {
		uid += 1

		return {
			isOpen: false,
			hovered: false,
			focused: false,
			positionRequest: 0,
			contentId: `tt-${uid}`,
			cleanupAutoUpdate: null,
			openTimeout: null,
			closeTimeout: null,
			floatingStyle: {
				position: 'fixed',
				left: '0px',
				top: '0px',
				transform: 'translate3d(0,0,0)',
				zIndex: this.zIndex,
				maxWidth:
					typeof this.maxWidth === 'number'
						? `${this.maxWidth}px`
						: this.maxWidth,
				pointerEvents: 'none',
				opacity: 0
			},
			arrowStyle: {}
		}
	},
	computed: {
		textAlignmentClass() {
			return {
				left: 'text-left',
				center: 'text-center',
				right: 'text-right'
			}[this.textAlign]
		},
		bodyClasses() {
			if (this.tone === 'dark-compact') {
				return 'bg-grey-950 text-white border border-grey-950 py-1.5 px-3 rounded-lg text-xs font-normal whitespace-nowrap'
			}
			return this.tone === 'light'
				? 'bg-white text-grey-1000 border border-grey-50 py-2 px-3 rounded-lg text-xs shadow-md'
				: 'bg-[#303030] text-white py-2 px-4 rounded-lg text-sm space-y-2'
		},
		arrowClasses() {
			if (this.tone === 'light') {
				return 'bg-white border-b border-r border-grey-100'
			}
			return this.tone === 'dark-compact' ? 'bg-grey-950' : 'bg-[#303030]'
		},
		isControlled() {
			return this.open !== null
		}
	},
	watch: {
		reference() {
			if (!this.isOpen) {
				return
			}

			// If reference changes, recompute and rebind autoUpdate
			this.stopPositioning()
			this.$nextTick(this.startPositioning)
		},
		open(value) {
			if (!this.isControlled) {
				return
			}

			this.isOpen = !!value && !this.disabled && !this._inactive
		},
		isOpen(value) {
			if (value) {
				this.$nextTick(this.startPositioning)
			} else {
				this.stopPositioning()
			}
		},
		placement() {
			if (this.isOpen) {
				this.updatePosition()
			}
		},
		offset() {
			if (this.isOpen) {
				this.updatePosition()
			}
		},
		zIndex(value) {
			this.floatingStyle = {
				...this.floatingStyle,
				zIndex: value
			}
		},
		maxWidth(value) {
			this.floatingStyle = {
				...this.floatingStyle,
				maxWidth: typeof value === 'number' ? `${value}px` : value
			}
		},
		disabled(value) {
			if (value) {
				this.reset()
			} else if (this.isControlled && !this._inactive) {
				this.isOpen = !!this.open
			}
		}
	},
	methods: {
		clearTimers() {
			if (this.openTimeout) {
				clearTimeout(this.openTimeout)
			}

			if (this.closeTimeout) {
				clearTimeout(this.closeTimeout)
			}

			this.openTimeout = null
			this.closeTimeout = null
		},
		onEnter(event) {
			if (this.disabled) {
				return
			}
			if (event && ['focus', 'focusin'].includes(event.type)) {
				this.focused = true
			} else {
				this.hovered = true
			}

			this.clearTimers()

			this.openTimeout = setTimeout(() => this.showNow(), this.openDelay)
		},
		onLeave(event) {
			if (this.disabled) {
				return
			}
			if (event && event.type === 'focusout') {
				if (event.currentTarget.contains(event.relatedTarget)) return
				this.focused = false
			} else {
				this.hovered = false
			}
			if (this.focused || this.hovered) return

			this.clearTimers()

			this.closeTimeout = setTimeout(
				() => this.hideNow(),
				this.closeDelay
			)
		},
		onTooltipEnter() {
			if (!this.interactive) {
				return
			}

			this.clearTimers()
		},
		onTooltipLeave() {
			if (!this.interactive) {
				return
			}

			this.onLeave()
		},
		showNow() {
			if (this.disabled || this._inactive || this._isBeingDestroyed) {
				return
			}

			if (!this.content && !this.$slots.content) {
				return
			}

			if (this.isControlled) {
				return
			}

			this.isOpen = true
		},
		hideNow() {
			this.clearTimers()
			if (this.isControlled) {
				return
			}

			this.isOpen = false
		},
		reset() {
			this.clearTimers()
			this.hovered = false
			this.focused = false
			this.isOpen = false
			this.stopPositioning()
		},
		getReferenceElement() {
			if (this.reference) {
				return this.reference
			}

			// tooltip root contains tooltip-trigger, but we want tooltip-trigger for hover/focus area. Reference should be the trigger span for correct geometry.
			return this.$el && this.$el.querySelector('.tooltip-trigger')
		},
		canPosition() {
			return (
				typeof window !== 'undefined' &&
				this.isOpen &&
				!this.disabled &&
				!this._inactive &&
				!this._isBeingDestroyed
			)
		},
		startPositioning() {
			if (!this.canPosition()) return
			this.stopPositioning()

			const referenceElement = this.getReferenceElement()
			const floatingElement = this.$refs.floatingElement

			if (!referenceElement || !floatingElement) {
				return
			}

			// interactive tooltips need pointer events enabled
			this.floatingStyle = {
				...this.floatingStyle,
				pointerEvents: this.interactive ? 'auto' : 'none',
				opacity: 1
			}

			this.updatePosition()

			const autoRef =
				referenceElement instanceof Element
					? referenceElement
					: referenceElement.contextElement

			// Auto re-position on scroll/resize/layout shifts
			if (autoRef && autoRef instanceof Element) {
				this.cleanupAutoUpdate = autoUpdate(
					autoRef,
					floatingElement,
					() => {
						this.updatePosition()
					}
				)
			}
		},
		stopPositioning() {
			// Ignore measurements still in flight after closing or rebinding.
			this.positionRequest += 1
			if (this.cleanupAutoUpdate) {
				this.cleanupAutoUpdate()
				this.cleanupAutoUpdate = null
			}

			this.floatingStyle = {
				...this.floatingStyle,
				opacity: 0,
				pointerEvents: 'none'
			}
		},
		async updatePosition() {
			if (!this.canPosition()) return
			const referenceElement = this.getReferenceElement()
			const floatingElement = this.$refs.floatingElement

			if (!referenceElement || !floatingElement) {
				return
			}
			const request = ++this.positionRequest

			const middleware = [offset(this.offset), shift({ padding: 0 })]

			if (this.flip) {
				middleware.push(flip())
			}

			const arrowElement = this.$refs.arrowElement

			if (this.arrow && arrowElement) {
				middleware.push(arrowMw({ element: arrowElement }))
			}

			const { x, y, placement, middlewareData } = await computePosition(
				referenceElement,
				floatingElement,
				{
					placement: this.placement,
					strategy: 'fixed',
					middleware
				}
			)
			if (request !== this.positionRequest) return

			this.floatingStyle = {
				...this.floatingStyle,
				top: `${Math.round(y)}px`,
				left: `${Math.round(x)}px`
			}

			if (this.arrow && arrowElement && middlewareData.arrow) {
				const { x: ax, y: ay } = middlewareData.arrow
				const side = placement.split('-')[0]

				// Position arrow on opposite side
				const staticSide = {
					top: 'bottom',
					right: 'left',
					bottom: 'top',
					left: 'right'
				}[side]

				this.arrowStyle = {
					left: ax != null ? `${Math.round(ax)}px` : '',
					top: ay != null ? `${Math.round(ay)}px` : '',
					right: '',
					bottom: '',
					[staticSide]: '-4px'
				}
			}
		}
	},
	mounted() {
		if (this.isControlled) {
			this.isOpen = !!this.open && !this.disabled
		}
	},
	activated() {
		if (this.isControlled) this.isOpen = !!this.open && !this.disabled
	},
	deactivated() {
		this.reset()
	},
	beforeDestroy() {
		this.reset()
	}
}
</script>
